import { validateUrl, type FieldResult } from "./validation";

export const MAX_DESCRIPTION_CHARS = 500;
export const MAX_LOGO_BYTES = 512 * 1024; // 512 KB
export const ALLOWED_LOGO_MIME = ["image/png", "image/jpeg", "image/webp", "image/gif"];

/** Plain-text description, length-limited, no HTML rendering (rendered as text). */
export function validateDescription(text: string): FieldResult {
  if (text.length > MAX_DESCRIPTION_CHARS) {
    return { ok: false, error: `Description is ${text.length}/${MAX_DESCRIPTION_CHARS} characters.` };
  }
  return { ok: true };
}

/** Only https/http links (reuses the shared URL validator that rejects javascript:/data:). */
export function validateLink(url: string): FieldResult {
  return validateUrl(url);
}

export interface LogoResult extends FieldResult {
  /** An object URL for local preview; caller MUST revoke it when done. */
  objectUrl?: string;
}

/**
 * Validate an image File for LOCAL preview only. The file is never uploaded and
 * never written on-chain. Returns an object URL the caller must revoke.
 */
export function validateLogoFile(file: File): LogoResult {
  if (!ALLOWED_LOGO_MIME.includes(file.type)) {
    return { ok: false, error: "Logo must be a PNG, JPEG, WebP, or GIF image." };
  }
  if (file.size > MAX_LOGO_BYTES) {
    return { ok: false, error: `Logo must be under ${Math.round(MAX_LOGO_BYTES / 1024)} KB.` };
  }
  return { ok: true, objectUrl: URL.createObjectURL(file) };
}

/** Collect validated metadata for the receipt (only non-empty values). */
export function collectMetadata(input: {
  description?: string;
  website?: string;
  x?: string;
  telegram?: string;
  hasLogo?: boolean;
}): Record<string, string> {
  const out: Record<string, string> = {};
  if (input.description?.trim()) out.description = input.description.trim();
  if (input.website?.trim()) out.website = input.website.trim();
  if (input.x?.trim()) out.x = input.x.trim();
  if (input.telegram?.trim()) out.telegram = input.telegram.trim();
  if (input.hasLogo) out.logo = "provided (local preview only, not stored on-chain)";
  return out;
}
